import { z } from "zod";
import { GAMES } from "../domain/games";

/**
 * How an in-progress game is hosted. Solo is the resumable mode today; `online`
 * is reserved (multiplayer resume is deferred). Kept in sync with the free-form
 * `mode` string on the Prisma `ActiveGame` model.
 */
export const GAME_MODES = ["solo", "online"] as const;
export type GameMode = (typeof GAME_MODES)[number];

/**
 * One in-progress game for the Home "continue playing" strip. Deliberately
 * small: `summary` is a free-form snapshot (phase / whose-turn / score) the strip
 * renders WITHOUT loading the full engine state. The web strip codes against this
 * exact shape; returned by `GET /me/active-games`.
 */
export const activeGameSummarySchema = z.object({
  /**
   * ITEM 1 — the saved game's own row id, and the ONLY way to name one.
   *
   * A player may hold several saves of the SAME game side by side, each with its own settings
   * (chess on medium AND chess on hard), so `game` no longer identifies a save. The strip sends
   * this back on the join to resume exactly the entry that was tapped. Optional so a client
   * built before this shipped still parses the response.
   */
  id: z.string().optional(),
  /** Which game (lowercase id; mirrors the Prisma `Game` enum). */
  game: z.enum(GAMES),
  /** How it was hosted. */
  mode: z.enum(GAME_MODES),
  /** The Colyseus roomId it was last persisted from (informational for solo resume). */
  roomId: z.string(),
  /**
   * CHESS ONLINE resume key — the STABLE `OnlineGame` id (a cuid, NOT the volatile
   * Colyseus roomId). Present only on a persisted online chess game; the web resumes
   * by joining `chess-online` with this as the `filterBy(["onlineGameId"])` value so
   * both players land in the SAME rehydrated room. Absent for solo / card games.
   */
  onlineGameId: z.string().optional(),
  /** Free-form Home-strip snapshot (e.g. `{ phase, turn, humanTurn, scores }`); may be null. */
  summary: z.unknown().nullable(),
  /**
   * ITEM 1 — the settings this game was STARTED with, so the strip can tell two saves of the
   * same game apart («شطرنج · سخت» vs «شطرنج · متوسط») instead of showing two identical rows.
   * Absent on rows saved before settings were recorded.
   */
  settings: z
    .object({
      difficulty: z.string().optional(),
      players: z.number().optional(),
      side: z.number().optional(),
    })
    .optional(),
  /** ISO-8601 timestamp of the last persist (list is sorted most-recent first). */
  updatedAt: z.string(),
});
export type ActiveGameSummary = z.infer<typeof activeGameSummarySchema>;

/** The caller's in-progress games, most-recent first. */
export const activeGamesResponseSchema = z.object({
  games: z.array(activeGameSummarySchema),
});
export type ActiveGamesResponse = z.infer<typeof activeGamesResponseSchema>;
